import { getTenantFromToken } from "config/helpers/common_helper";
import dayjs from "dayjs";
import { auth } from "firebase-setup";
import i18n from "multilingual";
import { toast } from "react-toastify";
import { hideLoader, showLoader } from "redux/actions/loaderActions";
import { store } from "redux/store";
import { SAVE_USER_SETTINGS } from "./ehr.service";

const { HTTPAgentService, FHIRSearch } = require("./http");

const currentUserSettings = () => {
  return store?.getState()?.auth?.currentUserSettings?.display;
};

export const EXECUTE_CHAT = async ({
  messageToSend,
  prompt_code,
  currentConversation,
}) => {
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/completions`,
      {
        request_text: messageToSend,
        prompt_code,
        persists_chat: currentUserSettings()?.persistSessionContext || false,
        conversation_id: currentConversation,
        agent_context: currentUserSettings()?.persistAgentContext || false,
        language:
          (currentUserSettings()?.language === "en" ? "en-US" : "es-ES") ||
          "en-US",
      },
      {
        // withCredentials: true,
        responseType: "stream",
        adapter: "fetch",
        headers: {
          Accept: "text/event-stream",
        },
      }
    );
    return response;
  } catch (e) {
    throw e;
  }
};

export const INITIALIZE_AGENT = async (params) => {
  const { agentId } = params;
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/initialize_agent`,
      {
        agent_id: agentId,
        language:
          (currentUserSettings()?.language === "en" ? "en-US" : "es-ES") ||
          "en-US",
        agent_context: currentUserSettings()?.persistAgentContext || false,
      }
    );
    return response;
  } catch (e) {
    console.log("some error g", e);
    throw e;
  }
};

export const GET_CONVERSATION_LIST = async (params) => {
  const { agentId } = params;
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/list_conversations`,
      {
        agent_id: agentId,
        language:
          (currentUserSettings()?.language === "en" ? "en-US" : "es-ES") ||
          "en-US",
        agent_context: currentUserSettings()?.persistAgentContext || false,
      }
    );
    return response;
  } catch (e) {
    console.log("some error g", e);
    throw e;
  }
};

export const DIAL_OUT = async (payload) => {
  try {
    const response = await HTTPAgentService.post(`/twilio/dial-out`, payload);
    return response;
  } catch (e) {
    toast?.error(i18n?.t("common.CALL_ASSIGN_ERROR"));
  }
};

export const AGENT_CHAT_HISTORY = async (params) => {
  const { agentId, conversationId } = params;
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/chat_history`,
      {
        agent_id: agentId,
        conversation_id: conversationId,
      }
    );
    return response;
  } catch (e) {}
};
export const GET_TOTAL_COST = async () => {
  try {
    const response = await HTTPAgentService.get(
      `/${getTenantFromToken()}/agents/get_usage`
    );
    return response;
  } catch (e) {
    console.error("error: ", e);
  }
};

export const GENERATE_CONVERSATION_SUMMARY = async (params) => {
  const { agentId, conversationId } = params;
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/generate_summary`,
      {
        agent_id: agentId,
        conversation_id: conversationId,
        language:
          (currentUserSettings()?.language === "en" ? "en-US" : "es-ES") ||
          "en-US",
      }
    );
    return response;
  } catch (e) {}
};

export const SEARCH_CHAT = async (searchKeyword = "") => {
  try {
    const response = await HTTPAgentService.post(
      `/${getTenantFromToken()}/agents/search_chat`,
      {
        search_text: searchKeyword,
      }
    );
    return response;
  } catch (e) {}
};

export const GET_PATIENT_LIST = async (params = "") => {
  store.dispatch(showLoader());
  try {
    const response = await FHIRSearch(`Patient?${params}`);
    if (response?.data?.total) {
      return response?.data;
    }
  } catch (e) {
    toast.error(i18n.t("common.PATIENT_DATA_NOT_FOUND"));
  } finally {
    store.dispatch(hideLoader());
  }
};

export const GET_COMPOSITIONS_LIST = async (params = "") => {
  store.dispatch(showLoader());
  try {
    const response = await FHIRSearch(`Composition?${params}`);
    if (response?.data?.total) {
      return response?.data;
    }
  } catch (e) {
    console.error("error: ", e);
  } finally {
    store.dispatch(hideLoader());
  }
};

export const GET_TASK_BY_PARAMS = async (params) => {
  try {
    const res = await FHIRSearch(`Task?${params}`);
    return res;
  } catch (e) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_QUESTIONNAIRE_RESPONSE = async (query = "") => {
  try {
    const response = await FHIRSearch(`QuestionnaireResponse${query}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const GET_QUESTIONNAIRE = async (query = "") => {
  try {
    const response = await FHIRSearch(`Questionnaire${query}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const GET_TASK_BY_TAG = async ({
  tag,
  count,
  pageToken = "",
  extraParams = "",
} = {}) => {
  try {
    // let query = `Task?_tag=${tag}`;
    let query = `Task?`;

    // Add count for page size
    if (count) {
      query += `&_count=${count}`;
    }

    // Add page token if provided
    if (pageToken) {
      query += `&_page_token=${pageToken}`;
    }

    if (extraParams) {
      query += extraParams;
    }

    query += `&_include=Task:subject`;

    // Fetch bundle
    const res = await FHIRSearch(query);
    return res;
  } catch (err) {
    // Google Cloud Healthcare API returns 400 with diagnostics like
    // "_has search failed: inner query on resource type 'Task', search
    // parameter '...' returned too many results to expand (>100)" — or
    // the equivalent for forward chaining (`subject.name=...`) — when a
    // chained / reverse-chained search's inner query expands past 100
    // results (SAM-2888). The chained Participants-tab search hits this
    // on any busy site, and the previous generic "common.ERROR" toast
    // was both unhelpful and fired repeatedly (once per keystroke).
    // Swap it for a single, refine-your-search hint when we detect this
    // specific diagnostic; everything else keeps the generic toast.
    const diagnostics =
      err?.response?.data?.issue?.[0]?.diagnostics ||
      err?.response?.data?.diagnostics ||
      "";
    if (/too many results to expand/i.test(diagnostics)) {
      toast.info(
        "Too many patients match that name — try a more specific search.",
        { toastId: "task-search-too-many" },
      );
    } else {
      toast.error(i18n.t("common.ERROR"));
    }
  }
};

export const GET_APPOINTMENTS = async (query = "") => {
  try {
    const response = await FHIRSearch(`Appointment${query}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const createUserConfiguration = async ({ organizationId = "" }) => {
  let configuration = {
    SMSreminder: {
      status: true,
      configuration: {
        values: [12, 3],
        unit: "hours",
      },
    },
  };

  let payload = {
    resourceType: "Basic",
    code: {
      coding: [
        {
          system: "http://enliv.com/code/basic-resource-type",
          code: "organization.preference.configuration",
        },
      ],
    },
    subject: {
      reference: `Organization/${organizationId}`,
    },
    created: dayjs().format("YYYY-MM-DD"),
    extension: [
      {
        url: "http://enliv.com/configuration/data",
        valueString: JSON.stringify(configuration),
      },
    ],
  };

  try {
    const response = await SAVE_USER_SETTINGS(`POST`, payload);
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};

export const GET_USER_CONFIGURATION = async ({ organizationId = "" }) => {
  try {
    let response = await FHIRSearch(
      `?resourceType=Basic&code=organization.preference.configuration&subject=Organization/${organizationId}`
    );
    return response;
  } catch (err) {
    toast.error(i18n.t("common.ERROR"));
  }
};
